<template>
    <div class="w-full">
        <div v-if="!reverse" class="grid" :class="gridClasses">
            <label
                v-if="label"
                :for="name"
                class="self-center justify-self-end pr-2"
            >
                <span class="text-sm font-bold whitespace-nowrap text-gray-900">
                    {{ label }}
                </span>
            </label>
            <div class="flex flex-col">
                <div class="flex items-center">
                    <div
                        v-if="prependSymbol"
                        class="flex h-8 items-center border border-r-0 border-gray-300 bg-gray-50 px-2 font-mono text-gray-500"
                    >
                        {{ prependSymbol }}
                    </div>
                    <input
                        :class="[
                            classes,
                            prependSymbol ? 'rounded-l-none' : '',
                            appendSymbol ? 'rounded-r-none' : '',
                            inputClass,
                        ]"
                        :placeholder="placeholder"
                        v-model="proxiedModel"
                        ref="input"
                        :name="name"
                        :type="inputType"
                        :step="step"
                        :disabled="disabled || readonly"
                        :autofocus="autofocus"
                        :tabindex="tabIndex"
                        @beforeinput="onBeforeInput"
                        @focus="onFocused()"
                        @blur="onBlurred()"
                    />
                    <div
                        v-if="$slots.append || appendSymbol"
                        class="flex h-8 items-center border border-l-0 border-gray-300 bg-gray-50 px-2 font-mono text-gray-500"
                    >
                        <slot name="append">
                            <div v-if="appendSymbol">
                                {{ appendSymbol }}
                            </div>
                        </slot>
                    </div>
                </div>
                <span
                    v-if="error"
                    class="mt-1 text-sm font-bold text-red-500"
                    >{{ error }}</span
                >
            </div>
        </div>
        <div v-else class="grid grid-cols-1">
            <div class="grid grid-cols-[3fr_1fr] items-center">
                <div class="flex items-center">
                    <div
                        v-if="$slots.prepend || appendSymbol"
                        class="flex h-8 items-center border border-l-0 border-gray-300 bg-gray-50 px-2 text-gray-500"
                    >
                        <slot name="prepend">
                            <div v-if="prependSymbol">
                                {{ prependSymbol }}
                            </div>
                        </slot>
                    </div>
                    <input
                        :class="[
                            reverseClasses,
                            prependSymbol ? 'rounded-l-none' : '',
                            appendSymbol ? 'rounded-r-none' : '',
                            inputClass,
                        ]"
                        :placeholder="placeholder"
                        v-model="proxiedModel"
                        ref="input"
                        :name="name"
                        :type="inputType"
                        :step="step"
                        :disabled="disabled || readonly"
                        :autofocus="autofocus"
                        :tabindex="tabIndex"
                        @beforeinput="onBeforeInput"
                        @focus="onFocused()"
                        @blur="onBlurred()"
                    />
                    <slot name="append">
                        <div
                            v-if="appendSymbol"
                            class="flex h-8 items-center border border-l-0 border-gray-300 bg-gray-50 px-2 text-gray-500"
                        >
                            {{ appendSymbol }}
                        </div>
                    </slot>
                </div>
                <label v-if="label" :for="name" class="pl-1">
                    <span
                        class="text-sm font-bold whitespace-nowrap text-gray-900"
                    >
                        {{ label }}
                    </span>
                </label>
            </div>
            <span v-if="error" class="mt-1 text-sm text-red-500">
                {{ error }}
            </span>
        </div>
    </div>
</template>

<script setup lang="ts">
import { formatNumberToAmerican, formatPercent } from '@/utils';
import { computed, nextTick, onMounted, ref } from 'vue';

const props = withDefaults(
    defineProps<{
        placeholder?: string;
        name?: string;
        label?: string;
        reverse?: boolean;
        type?: string;
        step?: string;
        error?: string;
        disabled?: boolean;
        prependSymbol?: string;
        appendSymbol?: string;
        autofocus?: boolean;
        readonly?: boolean;
        tabIndex?: number;
        inputClass?: string;
        // Only write back to the model on blur (Tab/Enter), not per keystroke.
        // Avoids fighting the user when the model round-trips through rounding.
        lazy?: boolean;
    }>(),
    {
        tabIndex: 0,
    },
);

const model = defineModel<string | number | null | undefined>({
    required: true,
});

const emit = defineEmits<{
    (name: 'change', val: any): void;
    (name: 'blur'): void;
}>();

const input = ref<HTMLInputElement | null>(null);

// Formatted fields stay text inputs even while focused: a native number input
// reports "" for a mid-edit value like "19." and resets the caret to the start.
// Because they're text inputs they'd otherwise accept any character (which then
// formats to NaN), so onBeforeInput rejects anything that isn't a valid number.
const formatted = props.type === 'price' || props.type === 'percent';
const inputType = formatted ? 'text' : props.type;

const focused = ref(false);

// While a lazy field is focused, edits live here and aren't committed to the
// model until blur.
const draft = ref<string | number | null | undefined>(null);

const proxiedModel = computed({
    set: (val: string | number | null | undefined) => {
        if (props.lazy && focused.value) {
            draft.value = val;
        } else {
            model.value = val;
        }
    },
    get: () => {
        if (props.lazy && focused.value) {
            return draft.value;
        }
        if (props.type === 'price' && model.value) {
            if (focused.value) {
                return model.value;
            } else {
                return formatNumberToAmerican(model.value as number);
            }
        } else if (props.type === 'percent' && model.value != null) {
            return focused.value
                ? model.value
                : formatPercent(model.value as number);
        } else {
            return model.value;
        }
    },
});

const gridClasses = computed(() => {
    return props.label ? 'grid-cols-[6rem_1fr]' : 'grid-cols-1';
});

const classes = computed(() => {
    const base = [
        'w-full',
        'h-8',
        'p-1',
        'shadow-sm',
        'disabled:bg-gray-200',
        'disabled:text-gray-500',
        'text-gray-800',
    ];

    if (
        props.type === 'number' ||
        props.type === 'price' ||
        props.type === 'percent'
    ) {
        base.push('font-mono');
    }

    if (props.error) {
        base.push(
            'border-red-300',
            'border-2',
            'focus:border-red-500',
            'focus:ring-1',
            'focus:ring-red-500',
        );
    } else {
        base.push(
            'border-gray-300',
            'focus:border-indigo-500',
            'focus:ring-1',
            'focus:ring-indigo-500',
        );
    }
    return base;
});

const reverseClasses = computed(() => {
    const base = ['w-full', 'h-8', 'p-1', 'shadow-sm'];

    if (props.error) {
        base.push(
            'border-red-300',
            'focus:border-red-500',
            'focus:ring-1',
            'focus:ring-red-500',
        );
    } else {
        base.push(
            'border-gray-300',
            'focus:border-indigo-500',
            'focus:ring-1',
            'focus:ring-indigo-500',
        );
    }
    return base;
});

onMounted(() => {
    if (input.value?.hasAttribute('autofocus')) {
        setTimeout(() => {
            input.value?.focus();
        }, 100);
    }
});

function onBeforeInput(e: Event) {
    if (!formatted) return;
    const ev = e as InputEvent;
    if (ev.data == null) return; // deletions / composition — always allow
    const el = ev.target as HTMLInputElement;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const next = el.value.slice(0, start) + ev.data + el.value.slice(end);
    // Allow partial numeric states: "", "-", ".", "19.", "-.5"
    if (!/^-?\d*\.?\d*$/.test(next)) ev.preventDefault();
}

function onFocused() {
    focused.value = true;
    if (props.lazy) {
        draft.value = model.value;
    }
    if (formatted) {
        // Highlight all so typing replaces the raw (unformatted) value.
        nextTick(() => input.value?.select());
    }
    emit('blur');
}

function onBlurred() {
    if (props.lazy && focused.value) {
        model.value = draft.value;
    }
    nextTick(() => {
        // Defer so the model commit lands before we re-format the display.
        focused.value = false;
    });
}

defineExpose({ focus: (opts?: FocusOptions) => input.value?.focus(opts) });
</script>

<style scoped>
/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

/* Firefox */
input[type='number'] {
    -moz-appearance: textfield;
}
</style>
